import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { STORAGE_NAMES } from '@root/app.config';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { Country } from '@root/app.interfaces';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { ShareService } from '@core/services/share-data/share-data.service';
import { BlindStateService } from '@core/services/blind-state/blind-state.service';
import { PartAvailabilityService } from '@core/services/part-availability/part-availability.service';
import { PaymentSessionServiceService } from '@core/services/payment-session-service/payment-session-service.service';
import { EngineService } from '@core/services/engine/engine.service';

export type ResetAppOptions = {
	country?: Country;
	navigationExtras?: NavigationExtras;
};

export type ResetServicesOptions = {
	engine?: boolean;
	blindDetection?: boolean;
	partAvailability?: boolean;
	paymentSession?: boolean;
};

@Injectable({
	providedIn: 'root',
})
export class ResetAppService {
	private resetSubject = new Subject<ResetAppOptions>();

	public readonly onReset$: Observable<ResetAppOptions> = this.resetSubject.asObservable();

	constructor(
		private router: Router,
		private engineService: EngineService,
		private activatedRoute: ActivatedRoute,
		private shareDataService: ShareService,
		private blindStateService: BlindStateService,
		private sessionStorageService: SessionStorageService,
		private partAvailabilityService: PartAvailabilityService,
		private paymentSessionService: PaymentSessionServiceService,
	) {}

	resetApp(options: ResetAppOptions): void {
		if (options.country) {
			this.sessionStorageService.setSession(options.country.code, STORAGE_NAMES.zip_country_code);
			this.shareDataService.setCountry(options.country.code);
		}

		const navigationExtras: NavigationExtras = {
			relativeTo: this.activatedRoute,
			queryParams: null,
			...options.navigationExtras,
		};

		this.router.navigate(['/home'], navigationExtras).then(() => {
			this.resetAllSessionStorageData();
			this.resetServices({ engine: true });

			this.resetSubject.next(options);
		});
	}

	resetServices(options: ResetServicesOptions): void {
		if (options.blindDetection) {
			this.shareDataService.setSelectOpeningMode(false);
			this.blindStateService.deleteAll();
		}

		if (options.partAvailability) {
			this.partAvailabilityService.resetAvailability();
		}

		if (options.paymentSession) {
			this.paymentSessionService.markAsUnpaid();
		}

		if (options.engine) {
			this.engineService.reset();
		}
	}

	resetAllSessionStorageData(): void {
		const storageKeys = [
			STORAGE_NAMES.zip_blind_config,
			STORAGE_NAMES.zip_blind_session_saved,
			STORAGE_NAMES.zip_blind_data,
			STORAGE_NAMES.zip_blind_text,
			STORAGE_NAMES.zip_blind_session,
			STORAGE_NAMES.zip_current_blind_id,
			STORAGE_NAMES.zip_last_opened_blind_id,
			STORAGE_NAMES.zip_blind_temp_session,
			STORAGE_NAMES.zip_blind_progress_saved,
			STORAGE_NAMES.zip_blind_type,
			STORAGE_NAMES.zip_image_visualisation_background,
			STORAGE_NAMES.zip_uploaded_image_visualisation_background,
			STORAGE_NAMES.zip_iv_screenshot,
			STORAGE_NAMES.zip_view_type,
			STORAGE_NAMES.zip_openings_data,
			STORAGE_NAMES.zip_detection_results,
			STORAGE_NAMES.zip_detection_image_size,
			STORAGE_NAMES.zip_quotation_form,
			STORAGE_NAMES.zip_contact_form,
		];
		storageKeys.forEach((key) => this.sessionStorageService.removeData(key));
	}

	resetEmbeddedLinkSessionStorageData(): void {
		this.sessionStorageService.removeBlindData(STORAGE_NAMES.zip_blind_data);
		this.sessionStorageService.removeBlindData(STORAGE_NAMES.zip_embedded_retailer);
	}

	resetBlindSessionStorageData(): void {
		this.sessionStorageService.removeData(STORAGE_NAMES.zip_blind_type);
	}
}
